// Story 2.10 — Reusable FR59 purchase confirm-modal.
//
// Implements UX-DR6 (confirm before consequence) + UX-DR24 (always-visible
// balance display) + FR59 (item + price + balance impact + post-purchase
// balance). State machine maps the backend's branched error codes
// (DOSSIER_PURCHASE_CAP_REACHED, DOSSIER_PURCHASE_DROP_CLOSED,
// INSUFFICIENT_BALANCE) onto distinct UI states with contextual CTAs.
//
// Default focus is the primary action button per UX-DR §1241 ("explicit
// choice required either way, no dark patterns"). aria-live="polite" on
// the action area announces state transitions to assistive tech.
//
// Lives in apps/web/ (not packages/components/) at v1 — promotion to the
// package happens when Epic 5 marketplace becomes the second consumer.

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  formatIssueNumber,
} from "@greenhold/components";

export type PurchaseConfirmModalStatus =
  | "idle"
  | "submitting"
  | "succeeded"
  | "cap_reached"
  | "drop_closed"
  | "error";

export type PurchaseConfirmModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemTitle: string;
  itemSubtitle?: string;
  /** UNIT price — the modal multiplies by `quantity` for totals. */
  priceCents: number;
  balanceCents: number | null;
  onConfirm: () => void | Promise<void>;
  onTopUp?: () => void;
  /** When set, modal renders the "Top up to buy" CTA instead of "Buy". */
  insufficientShortfallCents?: number;
  status: PurchaseConfirmModalStatus;
  errorMessage?: string;
  /** How many dossiers this checkout buys (default 1). */
  quantity?: number;
  /** When provided (with maxQuantity > 1), render the quantity stepper. */
  onQuantityChange?: (quantity: number) => void;
  maxQuantity?: number;
  /** When status='succeeded', show the minted edition badge. */
  successDossierEdition?: number;
  /** When status='succeeded' with quantity > 1, all minted editions. */
  successEditions?: number[];
  /** When status='cap_reached'. */
  capCount?: number;
  capLimit?: number;
  /** When status='drop_closed'. */
  dropCurrentStatus?: string;
  /** When status='cap_reached', the drop_id is used to build the marketplace link. */
  dropId?: string;
  /** Optional CTAs after success. */
  onViewVault?: () => void;
  onBackToShop?: () => void;
};

function formatEur(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "—";
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const euros = Math.floor(abs / 100);
  const remainder = abs % 100;
  return `${sign}€${euros}.${remainder.toString().padStart(2, "0")}`;
}

export function PurchaseConfirmModal(props: PurchaseConfirmModalProps) {
  const {
    open,
    onOpenChange,
    itemTitle,
    itemSubtitle,
    priceCents,
    balanceCents,
    onConfirm,
    onTopUp,
    insufficientShortfallCents,
    status,
    errorMessage,
    quantity = 1,
    onQuantityChange,
    maxQuantity = 1,
    successDossierEdition,
    successEditions,
    capCount,
    capLimit,
    dropCurrentStatus,
    dropId,
    onViewVault,
    onBackToShop,
  } = props;

  const totalPriceCents = priceCents * quantity;
  const balanceAfterCents =
    balanceCents !== null ? balanceCents - totalPriceCents : null;
  const showStepper = onQuantityChange !== undefined && maxQuantity > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Confirm your purchase</DialogTitle>
          <DialogDescription>
            {itemTitle}
            {itemSubtitle ? ` · ${itemSubtitle}` : ""}
          </DialogDescription>
        </DialogHeader>

        {/* Price/balance breakdown panel — always visible (UX-DR §1241). */}
        <dl className="rounded-md border border-border bg-muted/30 p-3 text-sm space-y-1">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Item</dt>
            <dd className="font-medium text-right">{itemTitle}</dd>
          </div>
          {showStepper ? (
            <div className="flex items-center justify-between">
              <dt className="text-muted-foreground">Quantity</dt>
              <dd className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Fewer dossiers"
                  onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1 || status === "submitting"}
                  className="grid h-6 w-6 place-items-center rounded border border-border leading-none disabled:opacity-30"
                  data-testid="purchase-qty-minus"
                >
                  −
                </button>
                <span data-testid="purchase-qty" className="min-w-4 text-center tabular-nums">
                  {quantity}
                </span>
                <button
                  type="button"
                  aria-label="More dossiers"
                  onClick={() =>
                    onQuantityChange(Math.min(maxQuantity, quantity + 1))
                  }
                  disabled={quantity >= maxQuantity || status === "submitting"}
                  className="grid h-6 w-6 place-items-center rounded border border-border leading-none disabled:opacity-30"
                  data-testid="purchase-qty-plus"
                >
                  +
                </button>
              </dd>
            </div>
          ) : null}
          <div className="flex justify-between">
            <dt className="text-muted-foreground">
              Price{quantity > 1 ? " (each)" : ""}
            </dt>
            <dd className="tabular-nums">{formatEur(priceCents)}</dd>
          </div>
          {quantity > 1 ? (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Total ({quantity})</dt>
              <dd className="tabular-nums">{formatEur(totalPriceCents)}</dd>
            </div>
          ) : null}
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Current balance</dt>
            <dd className="tabular-nums">{formatEur(balanceCents)}</dd>
          </div>
          <div className="flex justify-between font-semibold border-t border-border pt-1 mt-1">
            <dt>Balance after purchase</dt>
            <dd className="tabular-nums">{formatEur(balanceAfterCents)}</dd>
          </div>
        </dl>

        {/* Action area — aria-live polite so transitions are announced. */}
        <div aria-live="polite" className="mt-4 space-y-3">
          {insufficientShortfallCents !== undefined &&
          status === "idle" ? (
            <>
              <p className="text-sm text-muted-foreground">
                You need {formatEur(insufficientShortfallCents)} more to buy{" "}
                {quantity > 1 ? `these ${quantity} dossiers` : "this dossier"}.
              </p>
              <Button
                type="button"
                variant="primary"
                onClick={onTopUp}
                className="w-full"
                autoFocus
              >
                Top up to buy
              </Button>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="w-full text-sm underline decoration-dotted opacity-70 hover:opacity-100"
              >
                Not now
              </button>
            </>
          ) : null}

          {insufficientShortfallCents === undefined && status === "idle" ? (
            <>
              <Button
                type="button"
                variant="primary"
                onClick={onConfirm}
                className="w-full"
                autoFocus
              >
                {quantity > 1
                  ? `Buy ${quantity} sealed dossiers — ${formatEur(totalPriceCents)}`
                  : `Buy sealed dossier — ${formatEur(priceCents)}`}
              </Button>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="w-full text-sm underline decoration-dotted opacity-70 hover:opacity-100"
              >
                Not now
              </button>
            </>
          ) : null}

          {status === "submitting" ? (
            <Button
              type="button"
              variant="primary"
              disabled
              className="w-full"
            >
              Confirming purchase…
            </Button>
          ) : null}

          {status === "succeeded" ? (
            <div role="status" className="space-y-3">
              <div className="rounded-md border border-green-700/40 bg-green-50 p-4 text-sm">
                <p className="font-semibold text-green-900">
                  {successEditions && successEditions.length > 1
                    ? `Your ${successEditions.length} sealed dossiers are in your collection!`
                    : "Your sealed dossier is in your collection!"}
                </p>
                {successEditions && successEditions.length > 1 ? (
                  <p className="mt-1 text-green-800">
                    Issues #{formatIssueNumber(successEditions[0])}–#
                    {formatIssueNumber(successEditions[successEditions.length - 1])}{" "}
                    minted.
                  </p>
                ) : successDossierEdition !== undefined ? (
                  <p className="mt-1 text-green-800">
                    Issue #{formatIssueNumber(successDossierEdition)} minted.
                  </p>
                ) : null}
              </div>
              <Button
                type="button"
                variant="primary"
                onClick={onViewVault}
                className="w-full"
                autoFocus
              >
                View your collection
              </Button>
              <button
                type="button"
                onClick={onBackToShop}
                className="w-full text-sm underline decoration-dotted opacity-70 hover:opacity-100"
              >
                Back to shop
              </button>
            </div>
          ) : null}

          {status === "cap_reached" ? (
            <div className="space-y-3">
              <p className="text-sm">
                {capCount !== undefined &&
                capLimit !== undefined &&
                capCount < capLimit
                  ? `Buying ${quantity} would pass the per-user limit for this drop — you own ${capCount} of ${capLimit}, so up to ${capLimit - capCount} more can be bought.`
                  : `You've already bought the per-user limit${
                      capCount !== undefined && capLimit !== undefined
                        ? ` (${capCount} of ${capLimit})`
                        : ""
                    } for this drop.`}
              </p>
              {/* Story 2.10: forward-link to Epic 5 marketplace. 404s until
                  Epic 5 ships the /marketplace route. */}
              <a
                href={dropId ? `/marketplace?drop=${dropId}` : "/marketplace"}
                className="block w-full rounded-md bg-primary px-4 py-2 text-center text-primary-foreground hover:bg-primary/90"
              >
                See marketplace for resale ↑
              </a>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="w-full text-sm underline decoration-dotted opacity-70 hover:opacity-100"
              >
                Back to shop
              </button>
            </div>
          ) : null}

          {status === "drop_closed" ? (
            <div className="space-y-3">
              <p className="text-sm">
                This dossier drop has{" "}
                {dropCurrentStatus === "scheduled"
                  ? "not opened yet"
                  : "retired"}
                . Browse the marketplace for resale.
              </p>
              <Button
                type="button"
                variant="primary"
                onClick={onBackToShop ?? (() => onOpenChange(false))}
                className="w-full"
                autoFocus
              >
                Back to shop
              </Button>
            </div>
          ) : null}

          {status === "error" ? (
            <div className="space-y-3">
              <p className="text-sm text-destructive">
                {errorMessage ?? "Something went wrong. Try again."}
              </p>
              <Button
                type="button"
                variant="primary"
                onClick={onConfirm}
                className="w-full"
                autoFocus
              >
                Try again
              </Button>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="w-full text-sm underline decoration-dotted opacity-70 hover:opacity-100"
              >
                Cancel
              </button>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
